
import Vue from "vue";
import CupertinoAlertDialog, { CupertinoAlertDialogProps } from "@/components/ui/dialogs/CupertinoAlertDialog.vue";
import { genre as genreList, category as categoryList } from "@/lib/novelSettings";
import {
  getTranslatedGenreList,
  getTranslatedCategoryList,
} from "@/lib/utils/novelSettingsHelper";
import { Novel, NovelSetting } from "@/lib/models";
import { VueLoading } from "vue-loading-template";
import { Dialog } from "@/lib/utils";

interface Data {
  title: string;
  genre: NovelSetting | string | null;
  categories: NovelSetting[] | string[] | null;
  wordLength: number | null;
  genreList: NovelSetting[];
  categoryList: NovelSetting[];
  isCreating: boolean;
  unwatchAppTheme: (() => void) | null;
  unwatchEffectiveTheme: (() => void) | null;
}

interface Methods {
  onCreateButtonClick: () => Promise<void>;
  onChangeGenre: (event: Event) => void;
  addCategory: (item: NovelSetting) => void;
  removeCategory: (item: NovelSetting | string) => void;
  getCategoryName: (item: NovelSetting | string) => string;
  updateAppBackgroundColor: () => void;
  createNovel: (isSkipStart?: boolean) => Promise<void>;
  createManuscript: (novelId: String) => void;
  onSkipStart: () => Promise<void>;
}

interface Computed {
  isValidWordLength: boolean;
  canSubmit: boolean;
  translatedGenreList: NovelSetting[];
  translatedCategoryList: NovelSetting[];
  availableCategories: NovelSetting[];
}

export default Vue.extend<Data, Methods, Computed, {}>({
  components: {
    VueLoading,
  },

  mounted() {
    // 画面サイズが大きい場合に、画面下部の背景色が白くならないように
    // コンポーネントが表示されたときに背景色を変更
    this.updateAppBackgroundColor();
    
    // テーマ切り替え時に背景色を更新するためのwatcherを設定
    this.unwatchAppTheme = this.$store.watch(
      (state) => state.generalModule.appTheme,
      () => {
        this.updateAppBackgroundColor();
      }
    );
    this.unwatchEffectiveTheme = this.$store.watch(
      (state, getters) => getters["generalModule/effectiveTheme"],
      () => {
        this.updateAppBackgroundColor();
      }
    );
  },
  beforeDestroy() {
    // 他のコンポーネントに影響しないように、コンポーネントが破棄されるときに背景色を元に戻す
    document.getElementById("app")?.style.removeProperty("background-color");
    // watcherを解除
    if (this.unwatchAppTheme) {
      this.unwatchAppTheme();
    }
    if (this.unwatchEffectiveTheme) {
      this.unwatchEffectiveTheme();
    }
  },

  data() {
    return {
      title: "",
      genre: null,
      categories: null,
      wordLength: null,
      genreList,
      categoryList,
      isCreating: false,
      unwatchAppTheme: null,
      unwatchEffectiveTheme: null,
    };
  },

  methods: {
    updateAppBackgroundColor() {
      // ダークモード対応: CSS変数を使用
      const appElement = document.getElementById("app");
      if (appElement) {
        const computedStyle = getComputedStyle(document.documentElement);
        const bgColor = computedStyle.getPropertyValue("--color-bg").trim();
        appElement.style.setProperty("background-color", bgColor);
      }
    },
    async onCreateButtonClick() {
      try {
        this.isCreating = true;
        await this.createNovel();
      } catch (e) {
        // 特に何もしない
        console.log(e);
      } finally {
        this.isCreating = false;
      }
    },
    onChangeGenre(event: Event) {
      const target = event.target as HTMLSelectElement;
      const { value } = target;
      if (value === "") {
        this.genre = null;
      } else {
        const item = this.translatedGenreList.find((g) => g.id === value);
        if (item) this.genre = item;
      }
    },
    addCategory(item: NovelSetting) {
      // 最大3つまで制限
      if (this.categories && this.categories.length >= 3) {
        return;
      }
      // カテゴリを追加
      if (!this.categories) {
        this.categories = [item];
      } else {
        // 既に選択されている場合は追加しない
        const itemId = typeof item === 'string' ? item : item.id;
        if (this.categories.some((c) => (typeof c === 'string' ? c : c.id) === itemId)) {
          return;
        }
        this.categories = [...this.categories, item];
      }
    },
    removeCategory(item: NovelSetting | string) {
      // カテゴリを削除
      if (!this.categories) {
        return;
      }
      const itemId = typeof item === 'string' ? item : item.id;
      this.categories = this.categories.filter((c) => (typeof c === 'string' ? c : c.id) !== itemId);
      if (this.categories.length === 0) {
        this.categories = null;
      }
    },
    getCategoryName(item: NovelSetting | string): string {
      // カテゴリ名を取得（stringの場合は翻訳済みリストから取得）
      if (typeof item === 'string') {
        const category = this.translatedCategoryList.find((c) => c.id === item);
        return category ? category.name : item;
      }
      return item.name;
    },

    async createNovel(isSkipStart?: boolean) {
      const { isValidWordLength } = this;

      let { title, wordLength } = this;
      if (isSkipStart) {
        title = this.$t('novelCreatePage.defaultTitle');
        wordLength = null;
      } else if (!title) {
        return;
      }

      if (!isValidWordLength) {
        const completeDialog = new Dialog(CupertinoAlertDialog);
        const data: CupertinoAlertDialogProps = {
          title: this.$t('novelCreatePage.inputErrorTitle'),
          content: this.$t('novelCreatePage.inputErrorMessage'),
          action: this.$t('common.close'),
        };
        completeDialog.show(data);
        return;
      }

      let { genre, categories } = this;

      if (isSkipStart) {
        genre = null;
        categories = null;
      } else {
        if (genre && typeof genre !== "string") {
          genre = genre.id;
        }

        // カテゴリがNovelSetting[]の場合はIDの配列に変換
        if (categories && categories.length > 0 && typeof categories[0] !== "string") {
          categories = (categories as NovelSetting[]).map((category: NovelSetting) => category.id);
        }
      }

      const { dispatch } = this.$store;
      const { novelId } = (await dispatch("novelModule/createNovel", {
        title,
        genre,
        category: categories,
        wordLength,
        isSkip: isSkipStart,
      })) as Novel;

      if (wordLength) {
        const detail = [
          {
            name: "word-length",
            value: wordLength,
          },
        ];
        dispatch("novelThemeModule/updateTheme", { novelId, detail });
      }

      this.createManuscript(novelId);

      this.$ga.event("novel", "create-novel", "執筆をはじめる", 0);
      this.$router.push({ name: "theme", params: { novelId } });
    },
    createManuscript(novelId) {
      this.$store.dispatch("manuscriptModule/createManuscript", {
        novelId,
        title: "",
        content: "",
        manuscriptFolderId: null,
      });
    },
    async onSkipStart() {
      try {
        this.isCreating = true;
        await this.createNovel(true);
      } catch (e) {
        // 特に何もしない
        console.log(e);
      } finally {
        this.isCreating = false;
      }
    },
  },

  computed: {
    isValidWordLength() {
      const { wordLength } = this;
      const isNumber = (value: any) => !Number.isNaN(Number(value));
      if (wordLength && !isNumber(wordLength)) {
        return false;
      }

      return true;
    },
    canSubmit() {
      return this.title && this.genre !== null && this.categories !== null;
    },
    translatedGenreList() {
      return getTranslatedGenreList(this.$t);
    },
    translatedCategoryList() {
      return getTranslatedCategoryList(this.$t);
    },
    availableCategories() {
      // 選択されていないカテゴリのリスト
      const selectedIds = this.categories
        ? this.categories.map((item) => (typeof item === 'string' ? item : item.id))
        : [];
      return this.translatedCategoryList.filter((item) => !selectedIds.includes(item.id));
    },
  },
});
